"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseStoreSchema = exports.StorefrontPreferencesSchema = exports.CheckoutSettingsSchema = exports.ThemeColorScheme = exports.PaymentMethodsOfferedSchema = exports.SocialNetworkUrisSchema = exports.ReleaseMethodSchema = exports.ThemeColorSchemes = void 0;
const zod_1 = require("zod");
const __1 = require("..");
exports.ThemeColorSchemes = {
    [__1.Theme.Sushi]: {
        primary: '#72B945',
        secondary: '#509624',
        text: '#FFFFFF',
    },
    [__1.Theme.PuertoRico]: {
        primary: '#45B9AB',
        secondary: '#249681',
        text: '#FFFFFF',
    },
    [__1.Theme.SteelBlue]: {
        primary: '#4581B9',
        secondary: '#245F96',
        text: '#FFFFFF',
    },
    [__1.Theme.Studio]: {
        primary: '#7145B9',
        secondary: '#562496',
        text: '#FFFFFF',
    },
    [__1.Theme.Cerise]: {
        primary: '#E32A99',
        secondary: '#C0107A',
        text: '#FFFFFF',
    },
    [__1.Theme.BrickRed]: {
        primary: '#CC3156',
        secondary: '#A6152F',
        text: '#FFFFFF',
    },
    [__1.Theme.Tuscany]: {
        primary: '#CC5631',
        secondary: '#B43C09',
        text: '#FFFFFF',
    },
    [__1.Theme.Dixie]: {
        primary: '#DAA315',
        secondary: '#CB7C05',
        text: '#FFFFFF',
    },
    [__1.Theme.IrishCoffee]: {
        primary: '#663227',
        secondary: '#47150B',
        text: '#FFFFFF',
    },
    [__1.Theme.BaliHai]: {
        primary: '#8693B4',
        secondary: '#737F9C',
        text: '#FFFFFF',
    },
    [__1.Theme.Cobalt]: {
        primary: '#003FAF',
        secondary: '#2860C6',
        text: '#FFFFFF',
    },
    [__1.Theme.CodGray]: {
        primary: '#1D1D1D',
        secondary: '#313131',
        text: '#FFFFFF',
    },
};
exports.ReleaseMethodSchema = zod_1.z.object({
    delivery: zod_1.z.boolean().optional(),
    pickup: zod_1.z.boolean().optional(),
    description: __1.ShortStringSchema.optional(),
    /**
     * @description Digital products, like ebooks, music, etc.
     */
    digital: zod_1.z.boolean().optional(),
    /**
     * @description Room service, mainly for hotels.
     */
    room: zod_1.z.boolean().optional(),
    /**
     * @description The default option if none of the above are marked.
     */
    toBeArranged: zod_1.z.boolean().optional(),
});
exports.SocialNetworkUrisSchema = zod_1.z.object({
    instagram: __1.LongStringSchema.optional(),
    tiktok: __1.LongStringSchema.optional(),
    facebook: __1.LongStringSchema.optional(),
    twitter: __1.LongStringSchema.optional(),
    youtube: __1.LongStringSchema.optional(),
    kwai: __1.LongStringSchema.optional(),
    linkedIn: __1.LongStringSchema.optional(),
});
exports.PaymentMethodsOfferedSchema = zod_1.z.object({
    cash: zod_1.z.boolean().optional(),
    pix: zod_1.z.boolean().optional(),
    bankTransfer: zod_1.z.boolean().optional(),
    creditCard: zod_1.z.boolean().optional(),
    debitCard: zod_1.z.boolean().optional(),
    paymentLink: zod_1.z.boolean().optional(),
    boleto: zod_1.z.boolean().optional(),
});
exports.ThemeColorScheme = zod_1.z.object({
    primary: __1.ShortStringSchema,
    secondary: __1.ShortStringSchema,
    text: __1.ShortStringSchema,
});
exports.CheckoutSettingsSchema = zod_1.z.object({
    isCartEnabled: zod_1.z.boolean().optional(),
    isDeliveryAddressRequired: zod_1.z.boolean().optional(),
    isTaxIdRequired: zod_1.z.boolean().optional(),
    isPaymentMethodRequired: zod_1.z.boolean().optional(),
});
exports.StorefrontPreferencesSchema = zod_1.z.object({
    analytics: zod_1.z
        .object({
        googleAnalyticsPropertyId: __1.ShortStringSchema.optional(),
        metaPixelId: __1.ShortStringSchema.optional(),
    })
        .optional(),
    footer: zod_1.z
        .object({
        isTaglineEnabled: zod_1.z.boolean().optional(),
        copyright: __1.ShortStringSchema.optional(),
    })
        .optional(),
    spotlight: zod_1.z
        .object({
        enabled: zod_1.z.boolean(),
        title: __1.ShortestStringSchema.optional(),
        showPrices: zod_1.z.boolean().optional(),
    })
        .optional(),
});
/**
 * We've decided to create a base store schema without the owner and currentContract fields.
 * Adding those introduce a circular dependency between the store and user schemas which increases the schema complexity.
 */
exports.BaseStoreSchema = __1.BaseSchema.extend({
    companyName: __1.ShortStringSchema.optional(),
    companySector: __1.ShortStringSchema.optional(),
    companySectorName: __1.ShortStringSchema.optional(),
    customCompanySector: __1.ShortStringSchema.optional(),
    companySize: zod_1.z.nativeEnum(__1.ECompanySize).optional(),
    countryCode: zod_1.z.nativeEnum(__1.Alpha3Countries),
    currency: zod_1.z.nativeEnum(__1.Currency).optional(),
    locale: zod_1.z.string().optional(),
    timeZone: zod_1.z.string().optional(),
    isBillingAddressTheCompanyAddress: zod_1.z.boolean().optional(),
    email: __1.LongStringSchema.email().optional(),
    name: __1.ShortStringSchema,
    phoneNumber: __1.ShortStringSchema.or(__1.PhoneSchema).optional(),
    /**
     * The whatsapp phone number
     */
    whatsappPhoneNumber: __1.ShortStringSchema.or(__1.PhoneSchema).optional(),
    profileImageUri: __1.LongStringSchema.url().optional(),
    /**
     * Tax identification number (e.g. Brazil: CNPJ)
     */
    taxId: __1.ShortStringSchema.optional(),
    tradingName: __1.ShortStringSchema.optional(),
    storeOrigin: __1.OriginSchema.optional(),
    handle: zod_1.z.string().min(1).max(63).optional(),
    domain: __1.ShortStringSchema.optional(),
    isHandleChangeAllowed: zod_1.z.boolean(),
    hasMatureContent: zod_1.z.boolean().optional(),
    /**
     * If enabled, the store will display out of stock items on Storefront.
     */
    displayOutOfStockItems: zod_1.z.boolean().optional(),
    description: __1.LongStringSchema.optional(),
    aboutUs: __1.ExtraLongStringSchema.optional(),
    backgroundImageUri: __1.LongStringSchema.url().optional(),
    theme: zod_1.z.nativeEnum(__1.Theme).optional(),
    themeColorScheme: exports.ThemeColorScheme.optional(),
    internalSegment: __1.ShortStringSchema.optional(),
    releaseMethod: exports.ReleaseMethodSchema.optional(),
    status: zod_1.z.nativeEnum(__1.StoreStatus).optional(),
    checkoutSettings: exports.CheckoutSettingsSchema.optional(),
    /**
     * List of experiments the store is participating in.
     * This is used to enable/disable features for specific stores.
     * @example ['new-feature', 'freemium-plan']
     * @see
     * @link https://www.notion.so/Product-Experiments-3f55c0ed3b2245e4a1320bd0acc84c7a?pvs=4
     */
    experiments: zod_1.z.array(zod_1.z.string()).optional(),
    /**
     * @description The reason why the user chose to cancel the store.
     */
    cancellationReasonId: zod_1.z.string().optional(),
    cancellationReasonDetails: zod_1.z.string().optional(),
    lastFeedback: zod_1.z
        .object({
        createdAt: zod_1.z.coerce.date().optional(),
        rating: zod_1.z.number().min(0).max(10).optional(),
        ratingGroup: zod_1.z.nativeEnum(__1.FeedbackRatingGroup).optional(),
        hasAnsweredCurrentCycle: zod_1.z.boolean(),
        isAnswerEnabled: zod_1.z.boolean(),
    })
        .optional(),
    storefrontPreferences: exports.StorefrontPreferencesSchema.optional(),
});
//# sourceMappingURL=base-store.js.map